import React from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { EmbedPlayer } from './EmbedPlayer';
import { socialPlatforms } from '../../utils/socialPlatforms';
import { getEmbedUrl } from '../../utils/embedUtils';
import { getAnimationCSS } from '../../utils/customisation';

/* ─── Category filter tabs ─── */
export const CategoryTabs = ({ allLinks, activeCategory, setActiveCategory, ts, accent, anim, isLeft }) => {
          const categories = [...new Set(allLinks.map(l => l.category).filter(Boolean))];
          if (categories.length === 0) return null;
          return (
            <div className={`mb-4 flex flex-wrap gap-2 ${isLeft ? 'justify-start' : 'justify-center'}`} style={getAnimationCSS(anim, 1)} data-testid="category-tabs">
              <button onClick={() => setActiveCategory('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeCategory === 'all' ? 'opacity-100' : 'opacity-50 hover:opacity-75'}`}
                style={{ background: activeCategory === 'all' ? accent : `${ts['--theme-card']}`, color: activeCategory === 'all' ? (ts['--theme-accent-text'] || '#fff') : ts['--theme-text'], border: `1px solid ${ts['--theme-card-border']}` }}>
                All
              </button>
              {categories.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeCategory === cat ? 'opacity-100' : 'opacity-50 hover:opacity-75'}`}
                  style={{ background: activeCategory === cat ? accent : `${ts['--theme-card']}`, color: activeCategory === cat ? (ts['--theme-accent-text'] || '#fff') : ts['--theme-text'], border: `1px solid ${ts['--theme-card-border']}` }}>
                  {cat}
                </button>
              ))}
            </div>
          );
};

/* ─── Social icons row — recognised social platforms ─── */
export const SocialIconsRow = ({ allLinks, activeCategory, profile, ts, anim, isLeft, btnStyle, getIconColor, handleLinkClick }) => {
          const NON_SOCIAL_IDS = new Set(['custom', 'email', 'phone', 'sms', 'other']);
          const socialOnly = allLinks.filter(l => {
            if (activeCategory !== 'all' && l.category !== activeCategory) return false;
            if (NON_SOCIAL_IDS.has(l.platform)) return false;
            if (l.image_url && l.platform !== 'website') return false;
            // Normalise legacy layouts: anything other than the new 3 values
            // falls back to 'icon_only' (the free default).
            const layout = (l.layout === 'icon_in_button' || l.layout === 'both') ? l.layout : 'icon_only';
            if (layout === 'icon_in_button') return false; // pill-only
            return !!socialPlatforms.find(p => p.id === l.platform);
          });
          if (socialOnly.length === 0) return null;
          return (
            <div className="mb-8" style={getAnimationCSS(anim, 1)} data-testid="profile-links">
              <div className={`flex flex-wrap ${isLeft ? 'justify-start' : 'justify-center'} gap-3`}>
                {socialOnly.map((link, i) => {
                  const platform = socialPlatforms.find(p => p.id === link.platform);
                  const Icon = platform?.icon;
                  const cardStyle = profile.link_card_style || 'classic';
                  const isGlass = cardStyle === 'glass';
                  const isStacked = cardStyle === 'stacked';
                  return (
                    <button
                      key={link.id}
                      onClick={() => handleLinkClick(link)}
                      className={`group relative flex items-center justify-center w-14 h-14 overflow-hidden transition-all hover:scale-110 hover:shadow-lg ${
                        isGlass ? 'backdrop-blur-xl' : ''
                      } ${isStacked ? 'shadow-[0_4px_0_0_rgba(0,0,0,0.18),0_10px_24px_rgba(0,0,0,0.25)]' : ''}`}
                      style={{
                        background: isGlass
                          ? `${getIconColor(link.color)}22`
                          : `${getIconColor(link.color)}18`,
                        border: btnStyle.border ? `${isGlass ? '1px' : '2px'} solid ${getIconColor(link.color)}${isGlass ? '50' : '30'}` : 'none',
                        borderRadius: btnStyle.borderRadius,
                        ...(anim === 'stagger' ? getAnimationCSS('stagger', i + 2) : {})
                      }}
                      title={link.title || link.platformName}
                      data-testid={`profile-link-${link.id}`}
                    >
                      {link.image_url ? (
                        <img src={link.image_url} alt={link.platformName || link.title || ''} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        Icon && <Icon aria-label={link.platformName || link.title || 'Link'} role="img" className="w-7 h-7" style={{ color: getIconColor(link.color) }} />
                      )}
                      <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                        style={{ color: ts['--theme-text-muted'] }}>
                        {link.platformName || link.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
};

/* ─── Unified ordered content stack: embeds + pills + rich cards ─── */
export const LinkStack = ({ allLinks, activeCategory, profile, ts, accent, anim, btnStyle, handleLinkClick }) => {
          const NON_SOCIAL_IDS = new Set(['custom', 'email', 'phone', 'sms', 'other']);
          const stack = allLinks.filter(l => {
            if (activeCategory !== 'all' && l.category !== activeCategory) return false;
            const embeddable = !!getEmbedUrl(l.url) && l.embed_enabled !== false;
            if (embeddable) return true;
            // New 3-layout system: 'icon_in_button' or 'both' → render as pill below.
            if (l.layout === 'icon_in_button' || l.layout === 'both') return true;
            // Website behaves like a social platform: top-row icon unless layout says otherwise.
            if (l.platform === 'website') return false;
            // Rich content (legacy icon_top variant): show as rich card
            if (l.image_url) return true;
            if (NON_SOCIAL_IDS.has(l.platform)) return true;
            return false;
          });
          if (stack.length === 0) return null;
          return (
            <div className="space-y-4 mb-6" data-testid="rich-link-cards">
              {stack.map((link, i) => {
                const embeddable = !!getEmbedUrl(link.url) && link.embed_enabled !== false;
                const animStyle = anim === 'stagger' ? getAnimationCSS('stagger', i + 2) : getAnimationCSS(anim, 2);
                if (embeddable) {
                  return (
                    <ErrorBoundary key={link.id} fallback={null}>
                      <div style={animStyle}>
                        <EmbedPlayer link={link} themeStyles={ts} />
                      </div>
                    </ErrorBoundary>
                  );
                }
                const cardStyle = profile.link_card_style || 'classic';
                const isGlass = cardStyle === 'glass';
                const ctaText = (link.cta_text && link.cta_text.trim()) || 'Open';
                const ctaColor = link.cta_color || link.color || accent;
                const ctaTextColor = '#000';
                const platform = socialPlatforms.find(p => p.id === link.platform);
                const Icon = platform?.icon;
                const baseStyle = {
                  background: ts['--theme-card'],
                  border: `1px solid ${ts['--theme-card-border']}`,
                  ...animStyle,
                };

                // 'icon_in_button' OR 'both' → render as Linktree-style pill.
                if (link.layout === 'icon_in_button' || link.layout === 'both') {
                  return (
                    <button
                      key={link.id}
                      onClick={() => handleLinkClick(link)}
                      className={`w-full relative flex items-center gap-3 px-4 py-3 transition-all hover:scale-[1.01] active:scale-[0.99] ${isGlass ? 'backdrop-blur-xl' : ''}`}
                      style={{ ...baseStyle, borderRadius: btnStyle.borderRadius }}
                      data-testid={`profile-link-${link.id}`}
                    >
                      <span
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                        style={{ background: link.image_url ? 'transparent' : `${link.color || accent}22` }}
                      >
                        {link.image_url ? (
                          <img src={link.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          Icon && <Icon aria-hidden="true" className="w-5 h-5" style={{ color: link.color || accent }} />
                        )}
                      </span>
                      <span className="flex-1 text-center font-semibold text-sm sm:text-base" style={{ color: ts['--theme-text'] }}>
                        {link.title || link.platformName || 'Link'}
                      </span>
                      <span className="w-10 h-10 flex-shrink-0" aria-hidden="true" />
                    </button>
                  );
                }

                // Legacy / fallback: rich card for image-bearing or generic links
                return (
                  <div
                    key={link.id}
                    className={`rounded-2xl overflow-hidden transition-all hover:scale-[1.01] ${isGlass ? 'backdrop-blur-xl' : ''}`}
                    style={baseStyle}
                    data-testid={`rich-link-${link.id}`}
                  >
                    <div className="p-3 sm:p-4 flex items-start gap-3 sm:gap-4">
                      {link.image_url ? (
                        <img
                          src={link.image_url}
                          alt={link.title || ''}
                          className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover flex-shrink-0"
                          loading="lazy"
                          data-testid={`rich-link-image-${link.id}`}
                        />
                      ) : (
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex-shrink-0 flex items-center justify-center"
                          style={{ background: `${link.color || accent}22` }}>
                          {Icon ? <Icon aria-hidden="true" className="w-9 h-9" style={{ color: link.color || accent }} /> : null}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-base sm:text-lg leading-tight mb-1" style={{ color: ts['--theme-text'] }}>
                          {link.title || link.platformName || 'Link'}
                        </p>
                        {link.description && (
                          <p className="text-xs sm:text-sm leading-relaxed line-clamp-3" style={{ color: ts['--theme-text-muted'] }}>
                            {link.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                      <button
                        onClick={() => handleLinkClick(link)}
                        className="w-full py-3 rounded-xl font-semibold text-sm sm:text-base transition-all hover:opacity-90 active:scale-[0.98]"
                        style={{ background: ctaColor, color: ctaTextColor, border: 'none', borderRadius: btnStyle.borderRadius }}
                        data-testid={`rich-link-cta-${link.id}`}
                      >
                        {ctaText}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
};
